import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { DownloadSimple, Plus, Trash, Warning, UserPlus } from '@phosphor-icons/react';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const OMC_OPTS = ['INDANE', 'BHARAT GAS', 'HPCL'];

/* OpeningBalanceImportDialog
   Three modes:
     1. Manual — admin types each agency name (existing in dropdown), picks pending/advance, amount.
     2. Bulk paste — paste rows from Khatabook/Tally (CSV-ish). Auto-parses.
     3. Un-onboarded — free-text Agency Name + Mobile + OMC for agencies that
        haven't signed up yet. Stored in `pending_opening_balances` and auto-migrates
        when matching agency is created later.
*/
export default function OpeningBalanceImportDialog({ open, onClose, onImported }) {
  const [mode, setMode] = useState('manual');  // 'manual' | 'paste' | 'unonboarded'
  const [agencies, setAgencies] = useState([]);
  const [preview, setPreview] = useState([]);
  const [rows, setRows] = useState([]);  // {agency_id, kind, amount, note}
  const [pendingRows, setPendingRows] = useState([]);  // {agency_name, mobile, oil_company, kind, amount, note}
  const [pasteText, setPasteText] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [asOfDate, setAsOfDate] = useState('');

  const loadPreview = useCallback(async () => {
    try {
      const [pv, ag] = await Promise.all([
        api.get('/distributor-ledger/opening-balance/preview'),
        api.get('/agencies'),
      ]);
      setPreview(pv.data || []);
      setAgencies(ag.data || []);
    } catch (err) {
      toast.error('Preview load nahi hua');
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadPreview();
      setRows([{ agency_id: '', kind: 'pending', amount: '', note: '' }]);
      setPendingRows([{ agency_name: '', mobile: '', oil_company: '', kind: 'pending', amount: '', note: '' }]);
    }
  }, [open, loadPreview]);

  const addRow = () => setRows((r) => [...r, { agency_id: '', kind: 'pending', amount: '', note: '' }]);
  const updateRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const addPendingRow = () => setPendingRows((r) => [...r, { agency_name: '', mobile: '', oil_company: '', kind: 'pending', amount: '', note: '' }]);
  const updatePendingRow = (i, patch) => setPendingRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removePendingRow = (i) => setPendingRows((r) => r.filter((_, idx) => idx !== i));

  const parseUploadedFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading same file
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File 5MB se chhoti honi chahiye'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const r = await api.post('/pending-opening-balances/parse-file', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const parsed = r.data.rows || [];
      if (!parsed.length) { toast.error('Koi valid row file me nahi mili'); return; }
      setPendingRows(parsed.map((p) => ({
        agency_name: p.agency_name || '',
        mobile: p.mobile || '',
        oil_company: p.oil_company || '',
        kind: p.kind || 'pending',
        amount: p.amount || '',
        note: p.note || '',
      })));
      const w = (r.data.warnings || []).slice(0, 2).join(' • ');
      toast.success(`${parsed.length} rows file se parsed${w ? ' — ' + w : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'File parse failed');
    } finally { setBusy(false); }
  };

  const parsePastedText = () => {
    // Format: AGENCY_NAME, AMOUNT, KIND[, NOTE]   (one per line)
    // Or:    AGENCY_NAME | AMOUNT | KIND
    // KIND  : pending | advance | due | "+" / "-" prefix on amount
    const lines = pasteText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const parsed = [];
    const errors = [];
    for (const line of lines) {
      const parts = line.split(/\t|,|\|/).map((s) => s.trim()).filter(Boolean);
      if (parts.length < 2) { errors.push(`Skip "${line}" — minimum 2 columns chahiye`); continue; }
      const [agencyName, rawAmount, kindRaw, ...rest] = parts;
      const note = rest.join(', ');
      const cleaned = rawAmount.replace(/[₹,Rs.\s]/gi, '');
      let amount = Number(cleaned);
      let kind = (kindRaw || '').toLowerCase();
      // Handle signed amount: -500 → pending, +500 / 500 → advance default
      if (cleaned.startsWith('-')) { amount = Math.abs(amount); kind = kind || 'pending'; }
      else if (cleaned.startsWith('+')) { amount = Math.abs(amount); kind = kind || 'advance'; }
      else if (!kind) { kind = 'pending'; }   // default
      if (kind === 'due') kind = 'pending';
      if (!['pending', 'advance'].includes(kind)) { errors.push(`Skip "${line}" — kind 'pending' ya 'advance' hona chahiye`); continue; }
      if (!isFinite(amount) || amount <= 0) { errors.push(`Skip "${line}" — amount valid nahi`); continue; }

      const agency = agencies.find((a) => a.name.toLowerCase() === agencyName.toLowerCase())
                     || agencies.find((a) => a.name.toLowerCase().includes(agencyName.toLowerCase()));
      if (!agency) { errors.push(`Skip "${line}" — agency "${agencyName}" match nahi hua`); continue; }
      parsed.push({ agency_id: agency.id, agency_name: agency.name, kind, amount, note });
    }
    if (errors.length) toast.warning(`${errors.length} line skip — check console`, { description: errors.slice(0, 3).join(' • ') });
    if (errors.length) console.warn('Opening balance parse errors:', errors);
    setRows(parsed.map(({ agency_name: _agency_name, ...r }) => r));   // strip name
    if (parsed.length) toast.success(`${parsed.length} entries parsed — review and click Import`);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'unonboarded') {
        const valid = pendingRows.filter((r) => r.agency_name?.trim() && r.kind && Number(r.amount) > 0);
        if (!valid.length) { toast.error('Koi valid row nahi mili — Agency Name + Amount zaroori hai'); setBusy(false); return; }
        const payload = {
          entries: valid.map((r) => ({
            agency_name: r.agency_name.trim(),
            mobile: (r.mobile || '').trim(),
            oil_company: r.oil_company || '',
            kind: r.kind, amount: Number(r.amount),
            note: r.note || '', source: 'khatabook',
            as_of_date: asOfDate || undefined,
          })),
        };
        const r = await api.post('/pending-opening-balances/bulk', payload);
        const autoMsg = r.data.auto_migrated ? ` (${r.data.auto_migrated} auto-linked to existing agencies)` : '';
        toast.success(`${r.data.inserted} pending entries saved${autoMsg}`);
        onImported?.();
        onClose();
        return;
      }
      const valid = rows.filter((r) => r.agency_id && r.kind && Number(r.amount) > 0);
      if (!valid.length) { toast.error('Koi valid row nahi mili'); setBusy(false); return; }
      const payload = {
        entries: valid.map((r) => ({
          agency_id: r.agency_id, kind: r.kind, amount: Number(r.amount),
          note: r.note || '', source: mode === 'paste' ? 'khatabook' : 'manual',
          as_of_date: asOfDate || undefined,
        })),
        overwrite_existing: overwrite,
      };
      const r = await api.post('/distributor-ledger/bulk-opening-import', payload);
      toast.success(`${r.data.inserted} entries imported${r.data.skipped ? `, ${r.data.skipped} skipped` : ''}`);
      onImported?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  const validImportCount = mode === 'unonboarded'
    ? pendingRows.filter((r) => r.agency_name?.trim() && Number(r.amount) > 0).length
    : rows.filter((r) => r.agency_id && Number(r.amount) > 0).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DownloadSimple size={20} weight="duotone" className="text-violet-600" />
            Opening Balance Import — Khatabook / Tally
          </DialogTitle>
        </DialogHeader>

        <div className="bg-violet-50 border border-violet-200 rounded p-3 text-xs text-violet-900">
          📒 Old pending / advance amount distributor wallet me ek baar set kar do. Aaj se aage ka math system khud track karega.
          <ul className="list-disc ml-5 mt-1 text-[11px]">
            <li><b>Pending</b> = distributor humse paisa due hai (debit entry)</li>
            <li><b>Advance</b> = distributor ne extra paisa diya hai (credit entry)</li>
            <li><b>Un-onboarded</b> tab me agency abhi system me na ho — naam + mobile save kar do; jab agency create hogi tab khud auto-link ho jayegi.</li>
          </ul>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2">
          <button onClick={() => setMode('manual')} className={`flex-1 p-2 rounded border text-xs font-medium ${mode === 'manual' ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200'}`} data-testid="ob-mode-manual">Per-agency entry</button>
          <button onClick={() => setMode('paste')} className={`flex-1 p-2 rounded border text-xs font-medium ${mode === 'paste' ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200'}`} data-testid="ob-mode-paste">Bulk paste (CSV)</button>
          <button onClick={() => setMode('unonboarded')} className={`flex-1 p-2 rounded border text-xs font-medium flex items-center justify-center gap-1 ${mode === 'unonboarded' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200'}`} data-testid="ob-mode-unonboarded"><UserPlus size={14} /> Un-onboarded</button>
        </div>

        {/* Common: as-of date + overwrite */}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <label className="text-xs text-gray-700">As-of date (optional)</label>
            <Input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} data-testid="ob-as-of" />
            <div className="text-[10px] text-gray-500 mt-1">Khatabook me ki gayi balance ki original date</div>
          </div>
          {mode !== 'unonboarded' && (
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="ob-overwrite" />
                <Warning size={14} className="text-amber-600" />
                Overwrite existing opening balance entries
              </label>
            </div>
          )}
        </div>

        {/* Paste mode */}
        {mode === 'paste' && (
          <div className="space-y-2 mt-2">
            <label className="text-xs font-medium">Paste from Khatabook / Tally / Excel</label>
            <textarea rows={6} value={pasteText} onChange={(e) => setPasteText(e.target.value)} className="w-full border rounded p-2 font-mono text-xs"
              placeholder={`KISHORI INDANE, 25000, pending\nSHAKTI HP GAS DELHI, 8500, advance, Old credit\nRAJ GAS, -3500   (negative = pending auto-detect)`}
              data-testid="ob-paste-text" />
            <Button variant="outline" size="sm" onClick={parsePastedText} disabled={!pasteText.trim()} data-testid="ob-parse-btn">Parse rows</Button>
          </div>
        )}

        {/* Un-onboarded mode */}
        {mode === 'unonboarded' && (
          <div className="mt-2 border border-amber-200 rounded-lg overflow-hidden">
            <div className="bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900 flex items-center justify-between">
              <span>{pendingRows.length} pending entry(ies) — agency abhi system me NAHI hai</span>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer text-[11px] font-medium px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-1" data-testid="ob-upload-btn-label">
                  <DownloadSimple size={12} /> Upload CSV/Excel
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls,.txt"
                    onChange={parseUploadedFile}
                    className="hidden"
                    data-testid="ob-upload-input"
                  />
                </label>
                <Button size="sm" variant="outline" onClick={addPendingRow} className="h-6 text-xs gap-1"><Plus size={12} /> Row</Button>
              </div>
            </div>
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-amber-50 text-[10px] uppercase">
                  <tr>
                    <th className="px-2 py-1 text-left">Agency Name *</th>
                    <th className="px-2 py-1 text-left">Mobile</th>
                    <th className="px-2 py-1 text-left">OMC</th>
                    <th className="px-2 py-1 text-left">Kind</th>
                    <th className="px-2 py-1 text-right">Amount (₹)</th>
                    <th className="px-2 py-1 text-left">Note</th>
                    <th className="px-2 py-1 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRows.map((row, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-1 py-1">
                        <Input value={row.agency_name} onChange={(e) => updatePendingRow(i, { agency_name: e.target.value })} className="h-7 text-xs" placeholder="e.g. KISHORI INDANE" data-testid={`ob-pending-name-${i}`} />
                      </td>
                      <td className="px-1 py-1">
                        <Input value={row.mobile} onChange={(e) => updatePendingRow(i, { mobile: e.target.value })} className="h-7 text-xs" placeholder="98xxxxxxxx" data-testid={`ob-pending-mobile-${i}`} />
                      </td>
                      <td className="px-1 py-1">
                        <select value={row.oil_company} onChange={(e) => updatePendingRow(i, { oil_company: e.target.value })} className="w-full text-xs border rounded px-1 py-1" data-testid={`ob-pending-omc-${i}`}>
                          <option value="">--</option>
                          {OMC_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <select value={row.kind} onChange={(e) => updatePendingRow(i, { kind: e.target.value })} className={`w-full text-xs border rounded px-1 py-1 ${row.kind === 'pending' ? 'text-red-700' : 'text-emerald-700'}`} data-testid={`ob-pending-kind-${i}`}>
                          <option value="pending">Pending (debit)</option>
                          <option value="advance">Advance (credit)</option>
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <Input type="number" step="0.01" min={0} value={row.amount} onChange={(e) => updatePendingRow(i, { amount: e.target.value })} className="h-7 text-xs text-right" data-testid={`ob-pending-amount-${i}`} />
                      </td>
                      <td className="px-1 py-1">
                        <Input value={row.note} onChange={(e) => updatePendingRow(i, { note: e.target.value })} className="h-7 text-xs" placeholder="Optional" />
                      </td>
                      <td className="px-1 py-1 text-center">
                        <button onClick={() => removePendingRow(i)} className="text-red-600 p-0.5"><Trash size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-amber-50 px-3 py-1.5 text-[10px] text-amber-900 border-t border-amber-200">
              ℹ Auto-link tabhi hoga jab future me agency naam ≥ 88% match kare aur OMC same ho. Pending list "Pending Opening Balances" page me dikhegi.
            </div>
          </div>
        )}

        {/* Existing rows table — only for manual / paste modes */}
        {mode !== 'unonboarded' && (
          <div className="mt-2 border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 text-xs font-bold flex items-center justify-between">
              <span>{rows.length} row(s) ready to import</span>
              <Button size="sm" variant="outline" onClick={addRow} className="h-6 text-xs gap-1"><Plus size={12} /> Row</Button>
            </div>
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[10px] uppercase">
                  <tr>
                    <th className="px-2 py-1 text-left">Agency</th>
                    <th className="px-2 py-1 text-left">Kind</th>
                    <th className="px-2 py-1 text-right">Amount (₹)</th>
                    <th className="px-2 py-1 text-left">Note</th>
                    <th className="px-2 py-1 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-1 py-1">
                        <select value={row.agency_id} onChange={(e) => updateRow(i, { agency_id: e.target.value })} className="w-full text-xs border rounded px-1 py-1" data-testid={`ob-agency-${i}`}>
                          <option value="">-- select agency --</option>
                          {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <select value={row.kind} onChange={(e) => updateRow(i, { kind: e.target.value })} className={`w-full text-xs border rounded px-1 py-1 ${row.kind === 'pending' ? 'text-red-700' : 'text-emerald-700'}`} data-testid={`ob-kind-${i}`}>
                          <option value="pending">Pending (debit)</option>
                          <option value="advance">Advance (credit)</option>
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <Input type="number" step="0.01" min={0} value={row.amount} onChange={(e) => updateRow(i, { amount: e.target.value })} className="h-7 text-xs text-right" data-testid={`ob-amount-${i}`} />
                      </td>
                      <td className="px-1 py-1">
                        <Input value={row.note} onChange={(e) => updateRow(i, { note: e.target.value })} className="h-7 text-xs" placeholder="Optional" />
                      </td>
                      <td className="px-1 py-1 text-center">
                        <button onClick={() => removeRow(i)} className="text-red-600 p-0.5"><Trash size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Current opening balance summary */}
        {mode !== 'unonboarded' && preview.length > 0 && (
          <div className="mt-2">
            <div className="text-xs font-bold mb-1">Current opening balances ({preview.filter(p => p.entries_count > 0).length} of {preview.length} agencies)</div>
            <div className="border rounded max-h-40 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left">Agency</th>
                    <th className="px-2 py-1 text-right">Pending</th>
                    <th className="px-2 py-1 text-right">Advance</th>
                    <th className="px-2 py-1 text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.filter(p => p.entries_count > 0).map((p) => (
                    <tr key={p.agency_id} className="border-t">
                      <td className="px-2 py-1">{p.agency_name}</td>
                      <td className="px-2 py-1 text-right text-red-700">{fmt(p.current_opening_pending)}</td>
                      <td className="px-2 py-1 text-right text-emerald-700">{fmt(p.current_opening_advance)}</td>
                      <td className={`px-2 py-1 text-right font-bold ${p.net_opening >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(p.net_opening)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || validImportCount === 0} className={mode === 'unonboarded' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-violet-600 hover:bg-violet-700'} data-testid="ob-submit-btn">
            {busy ? 'Saving…' : mode === 'unonboarded' ? `Save ${validImportCount} pending entries` : `Import ${validImportCount} entries`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
